import { useEffect, useMemo, useState } from 'react';
import {
  isDeliveryHistorySuccess,
  listDeliveryHistorySorted,
  migrateDeliveryHistory,
  REPLAY_PASS_COST,
  type DeliveryHistoryEntry,
} from '../../../domain';
import { stopVinylMusic } from '../../assets/preloadSounds';
import { useAppStore } from '../../store/appStore';
import { deliveryDialogContentForGiant } from '../shared/deliveryDialogContent';
import {
  createDeliveryListScreenModel,
  type DeliveryListScreenProps,
  type DeliveryListStatusBanner,
} from './DeliveryListScreenModelScreen';
import { DeliveryListScreenController } from './DeliveryListScreenController';
import { DeliveryListScreenView } from './DeliveryListScreenView';
import { useInfoPopupState } from '../shared/infoPopup/infoPopupState';
import type { DeliveryCardItemFrame } from './delivery_card/DeliveryCard';

export type {
  DeliveryListScreenProps,
  DeliveryListStatusBanner,
} from './DeliveryListScreenModelScreen';

function deliveryListContentFromEntry(entry: DeliveryHistoryEntry) {
  const dialog = deliveryDialogContentForGiant(entry.giantId, entry.orderIndex);
  const isFailed = !isDeliveryHistorySuccess(entry);
  const statusBanner: DeliveryListStatusBanner = isFailed ? 'failed' : 'rating';
  const itemFrame: DeliveryCardItemFrame = isFailed ? 'closed' : 'opened';
  const deliveryRating =
    entry.lastRating?.totalScore ?? (entry.bestRating > 0 ? entry.bestRating : null);

  return {
    giantId: entry.giantId,
    orderIndex: entry.orderIndex,
    levelNameText: dialog.levelNameText,
    giantNameText: dialog.giantNameText,
    statusBanner,
    itemFrame,
    revealItem: !isFailed,
    deliveryRating,
  };
}

const EMPTY_DELIVERY_CONTENT = {
  giantId: 'roza' as const,
  orderIndex: 0,
  levelNameText: '',
  giantNameText: '',
  statusBanner: 'failed' as DeliveryListStatusBanner,
  itemFrame: 'closed' as DeliveryCardItemFrame,
  revealItem: false,
  deliveryRating: null,
};

export function DeliveryListScreen(props: DeliveryListScreenProps) {
  const infoPopup = useInfoPopupState();
  const playerEconomy = useAppStore((s) => s.playerEconomy);
  const passesBalance = useAppStore((s) => s.passesBalance);
  const deliveries = useMemo(
    () => listDeliveryHistorySorted(migrateDeliveryHistory(playerEconomy)),
    [playerEconomy],
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [againOpen, setAgainOpen] = useState(false);
  const safeSelectedIndex =
    deliveries.length === 0
      ? 0
      : Math.min(selectedIndex, deliveries.length - 1);
  const selectedEntry = deliveries[safeSelectedIndex];
  const content = selectedEntry
    ? deliveryListContentFromEntry(selectedEntry)
    : EMPTY_DELIVERY_CONTENT;

  useEffect(() => {
    return () => {
      stopVinylMusic();
    };
  }, []);

  const model = createDeliveryListScreenModel(props, {
    ...infoPopup,
    deliveries,
    selectedIndex: safeSelectedIndex,
    setSelectedIndex,
    againOpen,
    setAgainOpen,
    canReplay: passesBalance >= REPLAY_PASS_COST,
  }, content);
  const controller = new DeliveryListScreenController(model);

  return <DeliveryListScreenView model={model} controller={controller} />;
}
